import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import starIcon from '../images/star_icon.svg';
import driverLicense from '../images/driver-license.png';
import verifiedBadge from '../images/verified_badge.svg';
import zoomPlusIcon from '../images/zoom_plus.svg';

import BackButton from '../commonComponents/BackButton';
import { Colors } from '../themes/colors';
import TimerCountdown from '../commonComponents/TimerCountdown';
import { fetchJob, getAllSkillCategories, FETCH_JOB_ACTION } from '../actions';
import history from '../history';
import { extractSkills, isPartialShift } from '../commonComponents/CommonFunctions';
import FavoriteProfessionalIcon from '../images/icons/FavoriteProfessional';
import UserIcon from '../images/icons/User';
import WarningBanner from '../commonComponents/WarningBanner';
import { createLoadingSelector } from '../api/selectors';
import CustomSpinner from '../commonComponents/CustomSpinner';
import HeartIcon from '../images/icons/Heart';
import AttendanceBadge from './candidates/AttendanceBadge';
import { growthbook } from '../growthbook';

export default function CandidateList({ match }) {
  const dispatch = useDispatch();

  const user = useSelector((state) => state.user.user);
  const job = useSelector((state) => state.job.job);

  const allSkillCategories = useSelector(
    (state) => state.user.allSkillCategories,
  );

  const loadingSelector = createLoadingSelector([FETCH_JOB_ACTION]);

  const isLoading = useSelector((state) => loadingSelector(state));


  useEffect(() => {
    if (!job || job?.id !== match?.params?.jobId) {
      dispatch(fetchJob({ jobId: match?.params?.jobId }));
    }

    if (!allSkillCategories || allSkillCategories?.length === 0) {
      dispatch(getAllSkillCategories());
    }
  }, []);

  const renderRow = (title, value) => {
    if (value) {
      return (
        <div
          style={{
            display: 'flex',
            width: '100%',
            justifyContent: 'space-between',
            margin: '10px 0px',
          }}
        >
          <p className="global_font bold f-dark" style={{ margin: 0 }}>
            {title}
          </p>

          <p
            className="global_font f-dark"
            style={{
              margin: 0,
              maxWidth: '50%',
              textAlign: 'right',
              wordWrap: 'break-word',
            }}
          >
            {value}
          </p>
        </div>
      );
    }
  };

  return (
    <div className="component_container">
      <div
        className="floating_container"
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          paddingBottom: 120,
        }}
      >
        <div style={{ width: '95%' }}>
          <BackButton />
        </div>

        <h1 className="global_font f-dark" style={{ marginBottom: 30 }}>Candidates</h1>

        <div style={{ display: 'flex', columnGap: 20 }}>

          {isLoading ? <CustomSpinner isLoading={isLoading} /> : job?.multipleCounters?.map((item, index) => {
            const buttonRef = React.createRef(null);
            const { candidate } = item;

            const {
              userSpecialtySkills,
              userInsurances,
              jobInsurances,
              userOtherSkills,
            } = extractSkills(allSkillCategories, job, candidate);

            const licenseNumber = candidate?.license?.credentials?.find(
              ({ entry_field }) => entry_field?.license
            )?.entry_field?.license;
            const issueDate = candidate?.license?.credentials?.find(
              ({ entry_field }) => entry_field?.issue_date
            )?.entry_field?.issue_date;

            const specialtyMismatch = !userSpecialtySkills.some(({ code }) => code === job.skills[0]?.code) 
              && !job.skills[0]?.code?.includes('general');

            const insuranceMismatch = jobInsurances?.length > 0
              && !candidate?.skills?.some((skill) => jobInsurances?.find(({code}) => code === skill.code));

            const renderWarningDescription = () => {
              if (job.profession === 'DN') {
                let description = '';
                if (specialtyMismatch && insuranceMismatch) {
                  description = 'This professional does not have experience in all procedures needed or the same Insurance Provider.';
                } else if (specialtyMismatch) {
                  description = 'This professional does not have experience in all procedures needed.';
                } else if (insuranceMismatch) {
                  description = 'This professional does not have the same Insurance Provider.';
                }
                return (
                  <>
                    {description}
                  </>
                );
              }
              return (
                <>
                  This professional does not have your preferred specialty, <strong>{job.skills[0]?.name}</strong>.
                </>
              )
            }

            return (
              <div
                ref={(ref) => {
                  buttonRef.current = ref;
                }}
                role="button"
                style={{
                  border: item.isFavorite && `2px solid ${Colors.favorites_400}`,
                  position: 'relative',
                  width: 312,
                  padding: '25px 18px',
                  borderRadius: 17,
                  boxShadow:
                    '0px 0.698517px 1.39703px rgba(0, 0, 0, 0.3), 0px 2.09555px 6.98517px rgba(0, 0, 0, 0.2)',
                  transition: 'all 150ms ease-in-out',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}
                onClick={() => history.push(`/dashboard/confirm-hygienist/${job.id}/${candidate.id}`)}
                onKeyDown={() => history.push(`/dashboard/confirm-hygienist/${job.id}/${candidate.id}`)}
                onMouseEnter={() => {
                  buttonRef.current.style.transform = 'scale(1.02)';
                }}
                onMouseLeave={() => {
                  buttonRef.current.style.transform = '';
                }}
                tabIndex={index}
              >
                <div className="global_font f-red f-12 bold" style={{ textAlign: 'center', height: 30 }}>
                  {isPartialShift(item) ? 'AVAILABLE FOR PARTIAL SHIFT' : ''}
                </div>

                <div style={{ display: 'flex' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      ...(item.isFavorite && {
                        border: `3px solid ${Colors.favorites_400}`,
                        borderRadius: 100,
                        padding: 3,
                      }),
                      width: 75,
                      height: 75,
                      marginRight: 10,
                    }}
                  >
                    <img
                      src={candidate?.profile_image}
                      style={{
                        width: 75,
                        height: 75,
                        borderRadius: 100,
                        objectFit: 'cover',
                        border: `1px solid ${Colors.neutral_200}`,
                      }}
                      alt="profile_image"
                    />
                    {item.isFavorite && (
                      <div style={{ position: 'absolute', marginTop: 55, marginLeft: 65 }}>
                        <HeartIcon color={Colors.favorites_400} />
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div style={{ marginRight: 12 }}>
                        {item.isFavorite ? (
                          <FavoriteProfessionalIcon />
                        ) : (
                          <UserIcon color={Colors.secondary_500} />
                        )}
                      </div>

                      <h4
                        className="global_font f-dark f-18"
                        style={{ margin: 0 }}
                      >
                        {candidate?.first_name}
                        {' '}
                        {candidate?.last_name}
                      </h4>

                      <img
                        src={starIcon}
                        style={{
                          width: 15,
                          objectFit: 'cover',
                          margin: '0px 4px 0px 8px',
                        }}
                        alt="star_icon"
                      />

                      <h5
                        className="global_font f-dark"
                        style={{ margin: '1px 0px 0px 0px' }}
                      >
                        {candidate?.average_rating?.toFixed(1) || '5.0'}
                      </h5>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <img
                        src={driverLicense}
                        style={{
                          width: 24,
                          objectFit: 'cover',
                          marginRight: 12,
                        }}
                        alt="driver_license_icon"
                      />

                      <p className="global_font f-dark" style={{ margin: 0 }}>
                        Drivers License Verified
                      </p>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <img
                        src={verifiedBadge}
                        style={{
                          width: 24,
                          objectFit: 'cover',
                          marginRight: 12,
                        }}
                        alt="verified_badge_icon"
                      />

                      <p className="global_font f-dark" style={{ margin: 0 }}>
                        {candidate?.license?.profession}
                        {' '}
                        License Verified
                      </p>
                    </div>
                  </div>
                </div>

                <div style={{ width: '100%', position: 'relative' }}>
                  <div
                    style={{
                      backgroundColor: Colors.primary_500,
                      width: '100%',
                      height: 1,
                      margin: '25px 0px 25px 0px',
                      alignSelf: 'center',
                    }}
                  />

                  {growthbook.isOn('should-show-professional-stats') && (
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      right: 0,
                      transform: 'translateY(-50%)',
                      paddingRight: 10,

                    }}>
                      <AttendanceBadge
                        currentRank={candidate?.currentRank}
                        width={26}
                        height={26}
                        noLabel
                        disabled
                      />
                    </div>
                  )}
                </div>

                {(specialtyMismatch || insuranceMismatch) ? (
                  <WarningBanner
                    description={renderWarningDescription()}
                    indicatorStyle={{ display: 'none' }}
                    containerStyle={{
                      width: 'calc(100% + 36px)',
                    }}
                    iconStyle={{ marginLeft: 20 }}
                    primaryColor="#EA7E00"
                    secondaryColor="#FEFBF6"
                  />
                ): null}

                {renderRow('Profession:', job?.professionName)}
                {renderRow('License #:', licenseNumber)}
                {renderRow('Issued by State of:', candidate?.license?.state)}
                {issueDate && renderRow('Year Issued:', moment(issueDate).format('YYYY'))}
                {renderRow(
                  'Years of Experience:',
                  candidate?.years_of_experience,
                )}
                {renderRow('Education:', candidate?.education)}
                {candidate?.graduationDate && (
                  renderRow(
                    'Graduation Date:',
                    moment(candidate?.graduationDate).format('MM/DD/YYYY'),
                  )
                )}
                {renderRow(
                  <div style={{ display: 'flex' }}>
                    {job?.profession === 'DN' ? 'Procedures:' : 'Specialty Skills:'}

                    {!candidate?.skills?.some(({ code }) => code === job.skills[0]?.code) 
                    && !job.skills[0]?.code?.includes('general') 
                    && (
                      <svg width="20" height="18" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: 8 }}>
                        <path fillRule="evenodd" clipRule="evenodd" d="M7.90822 1.71858C8.82575 0.0874181 11.1743 0.0874173 12.0918 1.71858L18.7882 13.6232C19.6881 15.2231 18.532 17.1999 16.6964 17.1999H3.30364C1.46806 17.1999 0.311944 15.2231 1.21186 13.6232L7.90822 1.71858ZM11.1999 13.6C11.1999 14.2627 10.6626 14.8 9.9999 14.8C9.33716 14.8 8.7999 14.2627 8.7999 13.6C8.7999 12.9373 9.33716 12.4 9.9999 12.4C10.6626 12.4 11.1999 12.9373 11.1999 13.6ZM9.9999 3.99999C9.33716 3.99999 8.7999 4.53725 8.7999 5.19999V8.79999C8.7999 9.46274 9.33716 9.99999 9.9999 9.99999C10.6626 9.99999 11.1999 9.46274 11.1999 8.79999V5.19999C11.1999 4.53725 10.6626 3.99999 9.9999 3.99999Z" fill="#EA7E00"/>
                      </svg>
                    )}
                  </div>, userSpecialtySkills?.map(({name}) => name)?.join(', '))}
                {job?.profession === 'DN' && renderRow(
                  <div style={{ display: 'flex' }}>
                    Insurance:

                    {insuranceMismatch && (
                      <svg width="20" height="18" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: 8 }}>
                        <path fillRule="evenodd" clipRule="evenodd" d="M7.90822 1.71858C8.82575 0.0874181 11.1743 0.0874173 12.0918 1.71858L18.7882 13.6232C19.6881 15.2231 18.532 17.1999 16.6964 17.1999H3.30364C1.46806 17.1999 0.311944 15.2231 1.21186 13.6232L7.90822 1.71858ZM11.1999 13.6C11.1999 14.2627 10.6626 14.8 9.9999 14.8C9.33716 14.8 8.7999 14.2627 8.7999 13.6C8.7999 12.9373 9.33716 12.4 9.9999 12.4C10.6626 12.4 11.1999 12.9373 11.1999 13.6ZM9.9999 3.99999C9.33716 3.99999 8.7999 4.53725 8.7999 5.19999V8.79999C8.7999 9.46274 9.33716 9.99999 9.9999 9.99999C10.6626 9.99999 11.1999 9.46274 11.1999 8.79999V5.19999C11.1999 4.53725 10.6626 3.99999 9.9999 3.99999Z" fill="#EA7E00"/>
                      </svg>
                    )}
                  </div>, userInsurances?.map(({name}) => name)?.join(', '))}
                {renderRow('Skills:', userOtherSkills?.map(({name}) => name).join(', '))}
                {renderRow(
                  'Expires in:',
                  <TimerCountdown
                    expireTime={job?.multipleCounters?.find(
                      (counter) => counter?.candidate?.id === candidate?.id,
                    )?.expiration || new Date()}
                    allowFontScaling
                    style={{
                      color: Colors.error_500,
                      fontSize: 16,
                      textAlign: 'center',
                      fontFamily: 'Nunito',
                      fontWeight: 'bold',
                      marginVertical: 10,
                    }}
                  />,
                )}

                <div
                  style={{
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    top: '95%',
                    left: 150,
                    backgroundColor: Colors.primary_500,
                    width: 50,
                    height: 50,
                    borderRadius: '50%',
                    boxShadow:
                      '0px 0.698517px 1.39703px rgba(0, 0, 0, 0.3), 0px 2.09555px 6.98517px rgba(0, 0, 0, 0.2)',
                  }}
                >
                  <img src={zoomPlusIcon} alt="zoom_plus_icon" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
